import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { PlateFeature3D } from 'features/assetViz/objects/features/PlateFeature3D'
import {
  cartesianCircleGeometry,
  sphereGeometry,
} from 'features/assetViz/utils/geometryDefines'
import { extractScale } from 'features/assetViz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import { GeckoAssetModel, GeckoEllipsoidalHead } from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 * A surface representing an ellipsoidal head - an elliptical end cap
 * for pressure vessels with a 2:1 elliptical ratio. This provides
 * better stress distribution than hemispherical heads while being
 * less complex than torispherical heads.
 *
 * @param model       The ellipsoidal head component model
 * @param assetModel  The asset model containing this head
 * @param groupProps  Position, rotation, scale, and other 3D properties
 */
export const EllipsoidalHeadSurface3D: React.FC<
  {
    model: GeckoEllipsoidalHead
    assetModel?: GeckoAssetModel
  } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  const { show3D } = useAssetVizInternal()

  const { bounds, scale, plates } = React.useMemo(() => {
    const bounds = model.componentBounds

    // Ellipsoidal heads use radius and minor radius for elliptical scaling
    // Create elliptical shape with 2:1 ratio (radius in X/Z, minor radius in Y)
    const scale = extractScale(groupProps.scale).multiply(
      new Vector3(
        model.radius.num, // Width (X-axis)
        model.minorRadius.num, // Height (Y-axis) - half of radius for 2:1 ratio
        model.radius.num // Depth (Z-axis)
      )
    )

    return {
      bounds,
      scale,
      plates: model.platesArray,
    }
  }, [model, groupProps.scale])

  const geometry = React.useMemo(() => {
    if (!show3D) return cartesianCircleGeometry
    // Use sphere geometry but scale it elliptically to create 2:1 ellipsoid
    return sphereGeometry
  }, [show3D])

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      scale={scale}>
      {/* Render ellipsoidal head features using regular plates for elliptical geometry */}
      {plates.map((plate) => (
        <PlateFeature3D key={plate.id} model={plate} />
      ))}
    </ModelSurface3D>
  )
}
